import React, { useEffect, useMemo, useState } from 'react';
import {
  EntryKind,
  EntryPrivacy,
  DatePrecision,
  GraphMember,
  MemoryInput,
  useFamilyGraph,
} from '@/contexts/FamilyGraphContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';
import {
  X,
  Calendar,
  MapPin,
  Users as UsersIcon,
  Lock,
  Globe,
  BookOpen,
  HeartHandshake,
  Sparkles,
} from 'lucide-react';


interface Props {
  open: boolean;
  member: GraphMember;
  /** Pre-selected entry kind: memory, story, or life_event. Default 'memory'. */
  initialKind?: EntryKind;
  onClose: () => void;
  onSaved?: () => void;
}

const KIND_PRESETS: Record<EntryKind, { label: string; icon: React.ReactNode; placeholder: string; titleHint: string }> = {
  memory: {
    label: 'Memory',
    icon: <Sparkles className="w-4 h-4" />,
    placeholder: 'A moment, a feeling, a story you remember about them…',
    titleHint: 'e.g. "Sunday lunches at Grandma\'s"',
  },
  story: {
    label: 'Story',
    icon: <BookOpen className="w-4 h-4" />,
    placeholder: 'Tell the full story — set the scene, who was there, what happened…',
    titleHint: 'e.g. "How they met"',
  },
  life_event: {
    label: 'Life event',
    icon: <HeartHandshake className="w-4 h-4" />,
    placeholder: 'A milestone in their life — wedding, graduation, first job, retirement…',
    titleHint: 'e.g. "Wedding day"',
  },
};

// Two simple privacy options. The DB still understands a third 'shared' value
// for old rows, but we no longer expose it in the UI — every new memory is
// either visible to the whole KinsCircle, or to the uploader only. Nothing
// is ever public; RLS on family_graph_memories enforces this server-side.
const PRIVACY_OPTIONS: { value: EntryPrivacy; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'family', label: 'Family Only', description: 'Visible to your invited family circle', icon: <UsersIcon className="w-3.5 h-3.5" /> },
  { value: 'private', label: 'Only Me', description: 'Only you can see this', icon: <Lock className="w-3.5 h-3.5" /> },
];


const PRECISION_OPTIONS: { value: DatePrecision; label: string }[] = [
  { value: 'exact', label: 'Exact date' },
  { value: 'month', label: 'Month & year' },
  { value: 'year', label: 'Just the year' },
  { value: 'approximate', label: 'About this time' },
  { value: 'unknown', label: "I don't know when" },
];

const MemoryComposerModal: React.FC<Props> = ({
  open,
  member,
  initialKind = 'memory',
  onClose,
  onSaved,
}) => {
  const { members, addMemory } = useFamilyGraph();
  const [kind, setKind] = useState<EntryKind>(initialKind);
  const [title, setTitle] = useState('');
  const [eventDate, setEventDate] = useState<string>('');
  const [precision, setPrecision] = useState<DatePrecision>('exact');
  const [location, setLocation] = useState('');
  const [text, setText] = useState('');
  const [tagged, setTagged] = useState<string[]>([]);
  const [privacy, setPrivacy] = useState<EntryPrivacy>('family');
  const [tagFilter, setTagFilter] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset form whenever the modal opens for a new context.
  useEffect(() => {
    if (open) {
      setKind(initialKind);
      setTitle('');
      setEventDate('');
      setPrecision('exact');
      setLocation('');
      setText('');
      setTagged([]);
      setPrivacy('family');
      setTagFilter('');
    }
  }, [open, initialKind, member.id]);

  const taggables = useMemo(
    () =>
      members
        .filter((m) => m.id !== member.id && m.approvalStatus !== 'rejected')
        .filter((m) =>
          tagFilter ? m.name.toLowerCase().includes(tagFilter.toLowerCase()) : true
        )
        .slice(0, 12),
    [members, tagFilter, member.id]
  );

  const toggleTag = (id: string) => {
    setTagged((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!text.trim() && !title.trim()) {
      toast.error('Add a title or a story before saving.');
      return;
    }
    setBusy(true);
    try {
      const payload: MemoryInput = {
        text: text.trim(),
        title: title.trim() || null,
        eventDate: precision === 'unknown' || !eventDate ? null : eventDate,
        datePrecision: precision,
        location: location.trim() || null,
        taggedMemberIds: tagged,
        privacy,
        entryKind: kind,
      };
      await addMemory(member.id, payload);
      toast.success(
        kind === 'life_event'
          ? `Life event added to ${member.name.split(' ')[0]}'s journey`
          : kind === 'story'
          ? `Story saved`
          : `Memory saved`
      );
      onSaved?.();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const preset = KIND_PRESETS[kind];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-rose-500 via-fuchsia-500 to-amber-400 text-white p-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider opacity-90">
              {preset.label} for
            </div>
            <div className="font-serif font-bold text-lg truncate">{member.name}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-white/85 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Entry kind chooser */}
          <div className="grid grid-cols-3 gap-1.5">
            {(Object.keys(KIND_PRESETS) as EntryKind[]).map((k) => {
              const p = KIND_PRESETS[k];
              const active = k === kind;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`rounded-xl border px-2 py-2 text-xs font-bold flex items-center justify-center gap-1 transition ${
                    active
                      ? 'bg-fuchsia-600 text-white border-fuchsia-600 shadow'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-fuchsia-300 hover:bg-fuchsia-50'
                  }`}
                >
                  {p.icon}
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Title */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
              Title
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={preset.titleHint}
              className="mt-1"
            />
          </div>

          {/* Date + precision */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1">
                <Calendar className="w-3 h-3" /> Date
              </label>
              <Input
                type="date"
                value={eventDate}
                disabled={precision === 'unknown'}
                onChange={(e) => setEventDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
                Precision
              </label>
              <select
                value={precision}
                onChange={(e) => setPrecision(e.target.value as DatePrecision)}
                className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {PRECISION_OPTIONS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>
          {precision === 'unknown' && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              No problem — this will land in the "Undated memories" section. Family
              members can add the date later.
            </div>
          )}

          {/* Location */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> Location <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Lagos, Nigeria"
              className="mt-1"
            />
          </div>

          {/* Description / story */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
              {kind === 'life_event' ? 'What happened' : 'The story'}
            </label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={preset.placeholder}
              rows={4}
              className="mt-1 text-sm"
            />
          </div>

          {/* Tagged relatives */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1">
              <UsersIcon className="w-3 h-3" /> Tag relatives <span className="text-slate-400 font-normal normal-case">(optional)</span>
            </label>
            <Input
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              placeholder="Search relatives to tag…"
              className="mt-1"
            />
            {taggables.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {taggables.map((m) => {
                  const active = tagged.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggleTag(m.id)}
                      className={`text-[11px] px-2 py-1 rounded-full border transition ${
                        active
                          ? 'bg-fuchsia-600 text-white border-fuchsia-600'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-fuchsia-300'
                      }`}
                    >
                      {active ? '✓ ' : '+ '}{m.name}
                    </button>
                  );
                })}
              </div>
            )}
            {tagged.length > 0 && (
              <div className="text-[10px] text-slate-500 mt-1">
                {tagged.length} relative{tagged.length === 1 ? '' : 's'} tagged
              </div>
            )}
          </div>

          {/* Privacy */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1">
              <Globe className="w-3 h-3" /> Who can see this
            </label>
            <div className="mt-1 grid grid-cols-1 gap-1.5">
              {PRIVACY_OPTIONS.map((opt) => {
                const active = opt.value === privacy;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPrivacy(opt.value)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition ${
                      active
                        ? 'border-fuchsia-500 bg-fuchsia-50'
                        : 'border-slate-200 bg-white hover:border-fuchsia-300'
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center ${
                        active ? 'bg-fuchsia-600 text-white' : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {opt.icon}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-slate-800">{opt.label}</span>
                      <span className="block text-[11px] text-slate-500">{opt.description}</span>
                    </span>
                    <span
                      className={`w-4 h-4 rounded-full border-2 ${
                        active ? 'border-fuchsia-600 bg-fuchsia-600' : 'border-slate-300'
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 bg-white p-3 flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy}
            className="flex-1 bg-gradient-to-r from-rose-500 via-fuchsia-500 to-amber-400 text-white font-bold hover:opacity-95"
          >
            {busy ? 'Saving…' : `Save ${preset.label.toLowerCase()}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MemoryComposerModal;
